import {
  Button,
  capitalize,
  Checkbox,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import { couplerTypeToLocaleString } from "common/utils/enumTranslations";
import {
  ErrorMessage,
  Field,
  FieldArray,
  FieldProps,
  useFormikContext,
} from "formik";
import { FormikTextInput } from "common/components/FormikTextInput";
import { getOutputGuid } from "portal/utils/ids";
import { HydraulicCouplerType } from "protos/atk/enums";
import { NumberInput } from "common/components/NumberInput";
import { ScvOutput, TractorArchetype } from "protos/atk/autotractor";
import { StepProps } from "./StepProps";
import { TractorViewModel } from "../TractorViewModel";
import { useTranslation } from "react-i18next";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import React, { FC, useState } from "react";

export const ScvStep: FC<StepProps> = ({ disabled }) => {
  const formikProps = useFormikContext<TractorViewModel>();
  const { values } = formikProps;
  const { t } = useTranslation();

  const [checkedOverrideBox, setCheckedOverrideBox] = useState<boolean>(
    Boolean(formikProps.values.scvOutputs)
  );

  const hasCustomInfo =
    Boolean(formikProps.values.scvOutputs) || checkedOverrideBox;

  const isDisabled = disabled || !hasCustomInfo;
  return (
    <div className="flex flex-col gap-2">
      <Paper className="flex flex-col gap-2 p-4">
        <div className="flex gap-1">
          <InfoOutlinedIcon />
          <Typography>
            {capitalize(t("views.autotractor.equipment.customInfoBlurb"))}
          </Typography>
        </div>
        <FormControlLabel
          label={t("views.autotractor.equipment.tractors.hasCustomScvInfo")}
          checked={hasCustomInfo}
          disabled={disabled}
          control={<Checkbox />}
          onChange={() => {
            if (checkedOverrideBox) {
              formikProps.setFieldValue("scvOutputs", undefined);
            } else {
              formikProps.setFieldValue(
                "scvOutputs",
                values.archetype?.scvOutputs
              );
            }
            setCheckedOverrideBox(!checkedOverrideBox);
          }}
        />
      </Paper>
      {hasCustomInfo && (
        <FieldArray
          name="scvOutputs.scvOutput"
          render={(arrayHelpers) => (
            <Paper className="flex flex-col gap-2 p-4">
              <Typography className="font-bold">
                {capitalize(t("models.equipment.scv_short", { count: 1 }))}
              </Typography>
              <div className="flex flex-col gap-2">
                {values.scvOutputs?.scvOutput.map((v: ScvOutput, i) => {
                  return (
                    <div
                      key={`hitch-${i}`}
                      className="p-4 bg-opacity-10 bg-black rounded-lg flex flex-col gap-2"
                    >
                      <div className="flex justify-between">
                        <div className="flex flex-col">
                          <div className="flex gap-2">
                            <Typography>
                              {capitalize(t("models.equipment.port"))}
                            </Typography>
                            <Tooltip
                              title={t(
                                "views.autotractor.equipment.portInfoTooltip"
                              )}
                              placement="top"
                            >
                              <InfoOutlinedIcon
                                className="flex my-auto"
                                fontSize="small"
                              />
                            </Tooltip>
                          </div>
                          <NumberInput
                            textFieldProps={{ disabled: isDisabled }}
                            label=""
                            min={0}
                            onChange={(n) => {
                              formikProps.setFieldTouched(
                                `scvOutputs.scvOutput[${i}].port`,
                                true,
                                true
                              );
                              arrayHelpers.replace(i, { ...v, port: n });
                            }}
                            error=""
                            value={v.port}
                          />
                          <ErrorMessage
                            name={`scvOutputs.scvOutput[${i}].port`}
                            render={(e) => t(e)}
                          />
                        </div>
                        <IconButton
                          disabled={isDisabled}
                          color="info"
                          className="my-auto"
                          onClick={() => arrayHelpers.remove(i)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </div>
                      <div className="flex flex-col gap-4">
                        <div className="flex gap-2">
                          <FormikTextInput
                            name={`scvOutputs.scvOutput[${i}].description`}
                            label={capitalize(
                              t("utils.descriptors.description_one")
                            )}
                            disabled={isDisabled}
                          />
                          <div className="flex flex-col">
                            <Typography>
                              {capitalize(
                                t("models.equipment.couplerDiameter")
                              )}
                            </Typography>
                            <Field name={`scvOutputs.scvOutput[${i}].coupler`}>
                              {({
                                field,
                                meta,
                              }: FieldProps<number, TractorArchetype>) => (
                                <Select
                                  {...field}
                                  value={v.coupler}
                                  autoWidth
                                  size="small"
                                  disabled={disabled}
                                  error={Boolean(meta.error)}
                                >
                                  <MenuItem
                                    value={
                                      HydraulicCouplerType.HYD_COUPLER_ISO_5675_SIZE_12_5
                                    }
                                  >
                                    {couplerTypeToLocaleString(
                                      t,
                                      HydraulicCouplerType.HYD_COUPLER_ISO_5675_SIZE_12_5
                                    )}
                                  </MenuItem>
                                  <MenuItem
                                    value={
                                      HydraulicCouplerType.HYD_COUPLER_ISO_5675_SIZE_20
                                    }
                                  >
                                    {couplerTypeToLocaleString(
                                      t,
                                      HydraulicCouplerType.HYD_COUPLER_ISO_5675_SIZE_20
                                    )}
                                  </MenuItem>
                                </Select>
                              )}
                            </Field>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <Button
                className="mx-auto"
                variant="contained"
                startIcon={<AddIcon />}
                disabled={isDisabled}
                onClick={() =>
                  arrayHelpers.push(
                    ScvOutput.fromPartial({
                      id: getOutputGuid(),
                    })
                  )
                }
              >
                {t("views.autotractor.equipment.tractors.addScv")}
              </Button>
            </Paper>
          )}
        />
      )}
      {!hasCustomInfo && (
        <FieldArray
          name="archetype.scvOutputs.scvOutput"
          render={() => (
            <Paper className="flex flex-col gap-2 p-4">
              <Typography className="font-bold">
                {capitalize(t("models.equipment.scv_short", { count: 1 }))}
              </Typography>
              <div className="flex flex-col gap-2">
                {values.archetype?.scvOutputs?.scvOutput.map(
                  (v: ScvOutput, i) => {
                    return (
                      <div
                        key={`hitch-${i}`}
                        className="p-4 bg-opacity-10 bg-black rounded-lg flex flex-col gap-2"
                      >
                        <div className="flex justify-between">
                          <div className="flex flex-col">
                            <div className="flex gap-2">
                              <Typography>
                                {capitalize(t("models.equipment.port"))}
                              </Typography>
                              <Tooltip
                                title={t(
                                  "views.autotractor.equipment.portInfoTooltip"
                                )}
                                placement="top"
                              >
                                <InfoOutlinedIcon
                                  className="flex my-auto"
                                  fontSize="small"
                                />
                              </Tooltip>
                            </div>
                            <NumberInput
                              textFieldProps={{ disabled: true }}
                              label=""
                              min={0}
                              onChange={console.warn}
                              error=""
                              value={v.port}
                            />
                            <ErrorMessage
                              name={`archetype.scvOutputs.scvOutput[${i}].port`}
                              render={(e) => t(e)}
                            />
                          </div>
                          <IconButton
                            disabled={true}
                            color="info"
                            className="my-auto"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </div>
                        <div className="flex flex-col gap-4">
                          <div className="flex gap-2">
                            <div className="flex flex-col">
                              <Typography>
                                {capitalize(
                                  t("models.equipment.couplerDiameter")
                                )}
                              </Typography>
                              <Field
                                name={`scvOutputs.scvOutput[${i}].coupler`}
                              >
                                {({
                                  field,
                                  form,
                                }: FieldProps<string, TractorArchetype>) => (
                                  <Select
                                    {...field}
                                    value={v.coupler}
                                    autoWidth
                                    size="small"
                                    disabled
                                    error={
                                      form.touched.scvOutputs &&
                                      Boolean(form.errors.scvOutputs)
                                    }
                                  >
                                    <MenuItem
                                      value={
                                        HydraulicCouplerType.HYD_COUPLER_ISO_5675_SIZE_12_5
                                      }
                                    >
                                      {couplerTypeToLocaleString(
                                        t,
                                        HydraulicCouplerType.HYD_COUPLER_ISO_5675_SIZE_12_5
                                      )}
                                    </MenuItem>
                                    <MenuItem
                                      value={
                                        HydraulicCouplerType.HYD_COUPLER_ISO_5675_SIZE_20
                                      }
                                    >
                                      {couplerTypeToLocaleString(
                                        t,
                                        HydraulicCouplerType.HYD_COUPLER_ISO_5675_SIZE_20
                                      )}
                                    </MenuItem>
                                  </Select>
                                )}
                              </Field>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  }
                )}
              </div>
              <Button
                className="mx-auto"
                variant="contained"
                startIcon={<AddIcon />}
                disabled={true}
              >
                {t("views.autotractor.equipment.tractors.addScv")}
              </Button>
            </Paper>
          )}
        />
      )}
    </div>
  );
};
